import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Ban, Check, HeartPulse, MailPlus, Pencil, ShieldAlert, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "./StatusBadge";
import RequestTimeline from "./RequestTimeline";
import { fmtDate, fmtDateTime, daysBetween } from "@/lib/utils2";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function RequestDetailDialog({ request, onClose, onUpdated, supervisors = [], departments = [] }) {
  const { user } = useAuth();
  const [r, setR] = useState(request);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [clashes, setClashes] = useState({ hard_block: [], warnings: [] });
  const [overrideClash, setOverrideClash] = useState(false);
  const [deptChangePreview, setDeptChangePreview] = useState(null);

  // Manual resend state (admin only)
  const [resendOpen, setResendOpen] = useState(false);
  const [resendMode, setResendMode] = useState("supervisor"); // "supervisor" | "manual"
  const [resendSupId, setResendSupId] = useState("");
  const [resendManualEmail, setResendManualEmail] = useState("");
  const [resendManualName, setResendManualName] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [notifs, setNotifs] = useState([]);

  // Coverage-notify state (bundled with Approve): optionally CC a cover
  // supervisor with the full approved-absence summary + a free-text note.
  const [coverEnabled, setCoverEnabled] = useState(false);
  const [coverSupId, setCoverSupId] = useState("");
  const [coverNote, setCoverNote] = useState("");


  useEffect(() => {
    setR(request);
    setReason("");
    setEditing(false);
    setOverrideClash(false);
    setClashes({ hard_block: [], warnings: [] });
    setCoverEnabled(false);
    setCoverSupId("");
    setCoverNote("");
    setEdit(request ? {
      start_date: request.start_date,
      end_date: request.end_date,
      status: request.status,
      supervisor_id: request.supervisor_id,
      department: request.department,
      notes: request.notes || "",
    } : {});
  }, [request]);

  // Fetch clash info whenever a pending request is opened.
  useEffect(() => {
    if (!r || r.status !== "pending") return;
    let cancelled = false;
    api.post("/holiday-requests/check-clashes", {
      employee_id: r.employee_id || null,
      first_name: r.first_name,
      last_name: r.last_name,
      email: r.email,
      department: r.department,
      job_role: r.job_role,
      start_date: r.start_date,
      end_date: r.end_date,
      exclude_id: r.id,
    }).then(({ data }) => { if (!cancelled) setClashes(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [r?.id, r?.status]);

  // Fetch email history (original + manual resends). Placed before the early
  // return below so hook order stays consistent across renders.
  useEffect(() => {
    if (!r?.id || !user?.is_admin) return;
    (async () => {
      try {
        const { data } = await api.get(`/holiday-requests/${r.id}/notifications`);
        setNotifs(data?.notifications || []);
      } catch { /* silent — not critical */ }
    })();
  }, [r?.id, user?.is_admin]);

  const originalSupNotif = useMemo(() => {
    return notifs.find((n) =>
      !n.kind || n.kind === "supervisor_notification" || n.kind === "original_supervisor" ||
      (n.subject || "").toLowerCase().startsWith("new absence")
    );
  }, [notifs]);

  const manualResends = useMemo(
    () => notifs.filter((n) => n.kind === "manual_supervisor_resend"),
    [notifs],
  );

  const resendPreview = useMemo(() => {
    if (resendMode === "supervisor") {
      const s = supervisors.find((x) => x.id === resendSupId);
      return s ? { name: s.name, email: s.email } : null;
    }
    if (resendManualEmail.trim()) return { name: resendManualName.trim() || resendManualEmail.trim(), email: resendManualEmail.trim() };
    return null;
  }, [resendMode, resendSupId, resendManualEmail, resendManualName, supervisors]);

  if (!r) return null;
  const isPending = r.status === "pending";
  const canDecide = isPending && (user.is_admin || user.id === r.supervisor_id || (user.departments || []).includes(r.department));

  const decide = async (decision) => {
    if ((decision === "rejected" || decision === "cancelled") && !reason.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    if (decision === "approved" && coverEnabled && !coverSupId) {
      toast.error("Pick a coverage manager, or turn the cover option off");
      return;
    }
    setBusy(true);
    try {
      const body = { decision, reason: reason || undefined };
      if (decision === "approved" && user.is_admin && overrideClash) body.override_clash = true;
      if (decision === "approved" && coverEnabled && coverSupId) {
        body.cover_supervisor_id = coverSupId;
        const cn = coverNote.trim();
        if (cn) body.cover_note = cn;
      }
      const { data } = await api.patch(`/holiday-requests/${r.id}/decision`, body);
      setR(data); onUpdated?.(data);
      if (decision === "approved" && body.cover_supervisor_id) {
        const cov = supervisors.find((s) => s.id === body.cover_supervisor_id);
        toast.success(`Request approved · ${cov?.name || "coverage manager"} notified`);
      } else {
        toast.success(`Request ${decision}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "clash_hard_block") {
        toast.error(detail.message || "Clash detected — same contractor already has an overlap.");
        setClashes((c) => ({ ...c, hard_block: detail.clashes || c.hard_block }));
      } else {
        toast.error(errMsg(e, "Failed"));
      }
    }
    finally { setBusy(false); }
  };

  const saveAmend = async (override = null) => {
    // Defensive guard: if a React SyntheticEvent accidentally arrives here
    // (e.g. from `onClick={saveAmend}`), ignore it — spreading it into the
    // payload drags in DOM nodes/fiber refs and axios fails with a circular
    // JSON error before the request ever leaves the browser.
    const safeOverride =
      override && typeof override === "object" && !override.nativeEvent && !override.currentTarget
        ? override
        : null;
    // Merge any explicit override (from the confirm-amendment button) on top
    // of the current `edit` state so we never race with async setState.
    const effective = { ...edit, ...(safeOverride || {}) };
    const deptChanged = effective.department && effective.department !== r.department;
    // If the department is changing, resolve the new primary supervisor first
    // and show a confirmation dialog so admin sees what will change.
    if (deptChanged && !effective.__confirmed_dept) {
      try {
        const { data } = await api.get(`/admin/resolve-supervisor`, { params: { department: effective.department } });
        setDeptChangePreview({
          old_department: r.department,
          new_department: data.department,
          old_supervisor_name: r.supervisor_name,
          new_supervisor_name: data.default_supervisor?.name || "—",
          new_supervisor_id: data.default_supervisor_id,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Invalid department");
      }
      return;
    }
    setBusy(true);
    try {
      const days = daysBetween(effective.start_date, effective.end_date);
      const payload = { ...effective, days };
      delete payload.__confirmed_dept;
      if (reason) payload.reason = reason;
      if (effective.notify_employee) payload.notify_employee = true;
      const { data } = await api.patch(`/holiday-requests/${r.id}`, payload);
      setR(data); onUpdated?.(data);
      toast.success(effective.notify_employee ? "Request amended · email sent" : "Request amended");
      setEditing(false);
      setDeptChangePreview(null);
    } catch (e) { toast.error(errMsg(e, "Failed to amend request")); }
    finally { setBusy(false); }
  };

  const deleteRequest = async () => {
    setBusy(true);
    try {
      await api.delete(`/holiday-requests/${r.id}`);
      toast.success("Request deleted");
      onUpdated?.({ ...r, __deleted: true });
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    finally { setBusy(false); }
  };

  // Fetch email history (original + manual resends) whenever the request opens
  const openResend = () => {
    // Fresh idempotency key per open so re-opening the modal counts as a new send
    setResendKey(`resend-${r.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setResendMode("supervisor");
    setResendSupId("");
    setResendManualEmail("");
    setResendManualName("");
    setResendOpen(true);
  };

  const submitResend = async () => {
    setResendBusy(true);
    try {
      const payload = { idempotency_key: resendKey };
      if (resendMode === "supervisor") {
        if (!resendSupId) throw new Error("Please choose a supervisor");
        payload.supervisor_id = resendSupId;
      } else {
        if (!resendManualEmail.trim()) throw new Error("Please enter a recipient email");
        payload.recipient_email = resendManualEmail.trim();
        payload.recipient_name = resendManualName.trim() || null;
      }
      const { data } = await api.post(`/holiday-requests/${r.id}/notify-supervisor`, payload);
      const log = data?.email_log || {};
      toast.success(`Notification sent to ${log.recipient_name || log.to}\n${log.to}`);
      setResendOpen(false);
      // Refresh notifications list
      const { data: nn } = await api.get(`/holiday-requests/${r.id}/notifications`);
      setNotifs(nn?.notifications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to send");
    } finally {
      setResendBusy(false);
    }
  };


  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="request-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                {r.first_name} {r.last_name}
                {(r.request_type || "").toLowerCase() === "sickness" && (
                  <span
                    data-testid="sickness-badge-detail"
                    className="inline-flex items-center gap-1 rounded-full border border-rose-600 bg-rose-950/60 text-rose-200 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5"
                  >
                    <HeartPulse className="w-3 h-3"/> Sickness
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>{r.department}{r.site ? ` · ${r.site}` : ""} · {r.job_role}</DialogDescription>
            </div>
            <StatusBadge status={r.status} />
          </div>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3 border border-border rounded-md p-3">
            <KV label="Email" value={r.email}/>
            <KV label="Supervisor" value={r.supervisor_name}/>
            <KV label="Start" value={fmtDate(r.start_date)}/>
            <KV label="End" value={fmtDate(r.end_date)}/>
            <KV label="Days" value={r.days}/>
            <KV label="Submitted" value={fmtDateTime(r.submitted_at)}/>
            <KV label="Submitted as" value={r.job_role}/>
            <KV label="All contractor roles" value={
              (r.employee_roles && r.employee_roles.length)
                ? <span className="flex flex-wrap gap-1">{r.employee_roles.map((role, i) => (
                    <span key={role} className={`text-[11px] px-1.5 py-0.5 rounded ${i === 0 ? "bg-primary/10 text-primary font-medium" : "bg-muted text-muted-foreground"}`}>{role}</span>
                  ))}</span>
                : "—"
            }/>
            {r.decision_by_name && <KV label="Decided by" value={r.decision_by_name}/>}
            {r.decision_at && <KV label="Decided at" value={fmtDateTime(r.decision_at)}/>}
          </div>
          {r.notes && <div className="border border-border rounded-md p-3"><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</div>{r.notes}</div>}
          {r.decision_reason && <div className="border border-border rounded-md p-3"><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Reason</div>{r.decision_reason}</div>}

          {/* Clash banners — shown when reviewing pending requests */}
          {canDecide && !editing && (clashes.hard_block.length > 0 || clashes.warnings.length > 0) && (
            <div className="space-y-2" data-testid="clash-banner">
              {clashes.hard_block.length > 0 && (
                <div className="rounded-md border border-[#5E1F1F] bg-[#1F0A0A] p-3 text-[#F87171]" data-testid="clash-hard-banner">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="space-y-1.5 text-sm flex-1">
                      <div className="font-semibold">Hard block — same contractor already off these dates</div>
                      <ul className="text-xs space-y-0.5">
                        {clashes.hard_block.slice(0, 5).map((c) => (
                          <li key={c.id} data-testid={`clash-hard-${c.id}`}>
                            <StatusBadge status={c.status}/> {fmtDate(c.start_date)} → {fmtDate(c.end_date)} ({c.days}d) · {c.supervisor_name}
                          </li>
                        ))}
                      </ul>
                      {user.is_admin && (
                        <label className="flex items-center gap-2 pt-1 text-xs font-medium cursor-pointer select-none">
                          <Checkbox
                            data-testid="override-clash-checkbox"
                            checked={overrideClash}
                            onCheckedChange={(v) => setOverrideClash(!!v)}
                          />
                          Override and approve anyway (admin only)
                        </label>
                      )}
                      {!user.is_admin && (
                        <div className="text-xs">Only an admin can override and approve.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {clashes.warnings.length > 0 && (
                <div className="rounded-md border border-[#8A6E1F] bg-[#1F1808] p-3 text-[#FCD34D]" data-testid="clash-warn-banner">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="space-y-1 text-sm flex-1">
                      <div className="font-semibold">
                        {clashes.warnings.length} other {r.job_role} {clashes.warnings.length === 1 ? "person" : "people"} in {r.department} overlap these dates
                      </div>
                      <ul className="text-xs space-y-0.5">
                        {clashes.warnings.slice(0, 6).map((c) => (
                          <li key={c.id} data-testid={`clash-warn-${c.id}`}>
                            {c.first_name} {c.last_name} · {fmtDate(c.start_date)} → {fmtDate(c.end_date)} · <span className="opacity-70">{c.status}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Supervisor decision controls */}
          {canDecide && !editing && (
            <div className="border border-border rounded-md p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Decision</div>
              <Textarea data-testid="decision-reason-input" placeholder="Reason (required if rejecting or cancelling)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}/>

              {/* Optional: notify a coverage manager when approving */}
              <div className="rounded-md border border-border/70 bg-muted/20 p-2.5 space-y-2">
                <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none" data-testid="cover-toggle-label">
                  <Checkbox
                    data-testid="cover-toggle"
                    checked={coverEnabled}
                    onCheckedChange={(v) => setCoverEnabled(!!v)}
                  />
                  Also notify a coverage manager when approving
                </label>
                {coverEnabled && (
                  <div className="space-y-2 pl-6">
                    <Field label="Coverage manager">
                      <Select value={coverSupId} onValueChange={setCoverSupId}>
                        <SelectTrigger data-testid="cover-sup-trigger">
                          <SelectValue placeholder="Pick a supervisor to notify…"/>
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {supervisors
                            .filter((s) => s.id !== r.supervisor_id)
                            .map((s) => (
                              <SelectItem key={s.id} value={s.id} data-testid={`cover-sup-opt-${s.id}`}>
                                {s.name}{s.email ? ` · ${s.email}` : ""}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Note to coverage manager (optional)">
                      <Textarea
                        data-testid="cover-note-input"
                        value={coverNote}
                        onChange={(e) => setCoverNote(e.target.value)}
                        placeholder={`e.g. "Please cover ${r.first_name}'s ${r.job_role || "duties"} during these dates."`}
                        rows={2}
                      />
                    </Field>
                    <div className="text-[11px] text-muted-foreground">
                      Sent only when you press Approve. The email includes the full absence summary and a link back to this request.
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  data-testid="approve-btn"
                  disabled={busy || (clashes.hard_block.length > 0 && !(user.is_admin && overrideClash))}
                  onClick={() => decide("approved")}
                  className="gap-1.5"
                ><Check className="w-4 h-4"/> {coverEnabled && coverSupId ? "Approve & notify cover" : "Approve"}</Button>
                <Button data-testid="reject-btn" disabled={busy} variant="destructive" onClick={() => decide("rejected")} className="gap-1.5"><X className="w-4 h-4"/> Reject</Button>
                <Button data-testid="cancel-btn" disabled={busy} variant="outline" onClick={() => decide("cancelled")} className="gap-1.5"><Ban className="w-4 h-4"/> Cancel</Button>
              </div>
            </div>
          )}

          {/* Admin amend controls */}
          {user.is_admin && (
            <div className="border border-border rounded-md p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Admin amend</div>
                <Button data-testid="toggle-edit-btn" size="sm" variant={editing ? "secondary" : "outline"} onClick={() => setEditing(!editing)} className="gap-1.5">
                  <Pencil className="w-3.5 h-3.5"/> {editing ? "Cancel edit" : "Edit"}
                </Button>
              </div>
              {editing && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Start"><Input data-testid="edit-start" type="date" value={edit.start_date} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })}/></Field>
                    <Field label="End"><Input data-testid="edit-end" type="date" value={edit.end_date} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })}/></Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Status">
                      <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                        <SelectTrigger data-testid="edit-status"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {["pending","approved","rejected","cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Department">
                      <Select value={edit.department} onValueChange={(v) => setEdit({ ...edit, department: v, __confirmed_dept: false })}>
                        <SelectTrigger data-testid="edit-department"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {departments.map((d) => <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Supervisor">
                      <Select value={edit.supervisor_id} onValueChange={(v) => setEdit({ ...edit, supervisor_id: v })}>
                        <SelectTrigger data-testid="edit-supervisor"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Notes">
                    <Textarea data-testid="edit-notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={2}/>
                  </Field>
                  <Field label="Reason / change note">
                    <Input data-testid="edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="(optional) reason for amendment"/>
                  </Field>
                  <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
                    <Checkbox
                      data-testid="notify-employee-checkbox"
                      checked={!!edit.notify_employee}
                      onCheckedChange={(v) => setEdit({ ...edit, notify_employee: !!v })}
                    />
                    Email the contractor about the new dates
                  </label>
                  <Button data-testid="save-amend-btn" onClick={() => saveAmend()} disabled={busy} className="w-full">Save amendments</Button>
                </div>
              )}

              {/* Dept-change confirmation (opens after saveAmend detects a dept change) */}
              <AlertDialog open={!!deptChangePreview} onOpenChange={(o) => !o && setDeptChangePreview(null)}>
                <AlertDialogContent data-testid="dept-change-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm department amendment</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="text-sm">
                        Changing the department will re-route this request to the new primary supervisor.
                        No email will be sent — use "Resend to supervisor" afterwards if you want to notify them.
                        <div className="mt-3 space-y-1.5 border border-border rounded-md p-3 bg-muted/40 text-foreground">
                          <div><span className="text-muted-foreground">Current department:</span> <span className="font-medium">{deptChangePreview?.old_department}</span></div>
                          <div><span className="text-muted-foreground">New department:</span> <span className="font-medium">{deptChangePreview?.new_department}</span></div>
                          <div><span className="text-muted-foreground">Current supervisor:</span> <span className="font-medium">{deptChangePreview?.old_supervisor_name || "—"}</span></div>
                          <div><span className="text-muted-foreground">New primary supervisor:</span> <span className="font-medium">{deptChangePreview?.new_supervisor_name}</span></div>
                        </div>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="dept-change-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="dept-change-confirm-btn"
                      onClick={() => {
                        // Persist the amendment synchronously with an explicit
                        // override — avoids the stale-closure trap that comes
                        // from setEdit(...) + setTimeout(saveAmend, 0).
                        const override = {
                          supervisor_id: deptChangePreview.new_supervisor_id,
                          department: deptChangePreview.new_department,
                          __confirmed_dept: true,
                        };
                        setEdit((e) => ({ ...e, ...override }));
                        setDeptChangePreview(null);
                        saveAmend(override);
                      }}
                    >
                      Confirm amendment
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          <RequestTimeline requestId={r.id} refreshKey={r.status + (r.decision_at || "") + (r.audit_trail?.length || 0)}/>

          {/* Notifications history — admin only */}
          {user.is_admin && notifs.length > 0 && (
            <div className="border border-border rounded-md p-3 space-y-2" data-testid="notification-history">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Notifications</div>
              <div className="space-y-1.5 text-sm">
                {originalSupNotif && (
                  <div>
                    <span className="text-muted-foreground">Original: </span>
                    <span className="font-medium">{originalSupNotif.recipient_name || originalSupNotif.to}</span>
                    <span className="text-muted-foreground"> — {fmtDateTime(originalSupNotif.sent_at)}</span>
                  </div>
                )}
                {manualResends.map((n) => (
                  <div key={n.id} data-testid={`manual-resend-${n.id}`}>
                    <span className="text-muted-foreground">Manual: </span>
                    <span className="font-medium">{n.recipient_name || n.to}</span>
                    <span className="text-muted-foreground"> — {fmtDateTime(n.sent_at)}
                      {n.triggered_by_name ? ` · by ${n.triggered_by_name}` : ""}
                      {n.status === "failed" ? " · failed" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex w-full items-center justify-between gap-2 flex-wrap">
            {user.is_admin ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="resend-supervisor-btn"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={openResend}
                  className="gap-1.5"
                >
                  <MailPlus className="w-4 h-4" /> Resend to supervisor
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid="delete-request-btn"
                      variant="destructive"
                      size="sm"
                      disabled={busy}
                      className="gap-1.5"
                    >
                      <Trash2 className="w-4 h-4" /> Delete request
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-request-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this absence notification?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes {r.first_name} {r.last_name}'s request for {fmtDate(r.start_date)} → {fmtDate(r.end_date)}. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-request-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="delete-request-confirm-btn"
                        onClick={deleteRequest}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete permanently
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : <span />}
            <Button data-testid="close-detail-btn" variant="outline" onClick={onClose}>Close</Button>
          </div>
        </DialogFooter>

        {/* Manual resend modal */}
        <Dialog open={resendOpen} onOpenChange={(o) => !resendBusy && setResendOpen(o)}>
          <DialogContent className="max-w-lg" data-testid="resend-supervisor-modal">
            <DialogHeader>
              <DialogTitle>Resend supervisor notification</DialogTitle>
              <DialogDescription>
                Sends another supervisor notification for this existing request.
                It does <strong>not</strong> create a new request, and it does <strong>not</strong> change the assigned supervisor.
              </DialogDescription>
            </DialogHeader>

            <div className="text-sm space-y-2 border border-border rounded-md p-3 bg-muted/40">
              <div><span className="text-muted-foreground">Contractor:</span> <span className="font-medium">{r.first_name} {r.last_name}</span></div>
              <div><span className="text-muted-foreground">Department:</span> <span className="font-medium">{r.department}</span></div>
              <div><span className="text-muted-foreground">Current status:</span> <span className="font-medium capitalize">{r.status}</span></div>
              <div><span className="text-muted-foreground">Original supervisor:</span> <span className="font-medium">{r.supervisor_name || "—"}</span></div>
              {originalSupNotif && (
                <div><span className="text-muted-foreground">Original notification sent:</span> <span className="font-medium">{fmtDateTime(originalSupNotif.sent_at)}</span></div>
              )}
            </div>

            <div className="space-y-3 pt-1">
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="resend-mode" checked={resendMode === "supervisor"} onChange={() => setResendMode("supervisor")} data-testid="resend-mode-supervisor"/>
                  Pick a supervisor
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="resend-mode" checked={resendMode === "manual"} onChange={() => setResendMode("manual")} data-testid="resend-mode-manual"/>
                  Manual email override
                </label>
              </div>

              {resendMode === "supervisor" && (
                <Field label="Recipient supervisor">
                  <Select value={resendSupId} onValueChange={setResendSupId}>
                    <SelectTrigger data-testid="resend-supervisor-select"><SelectValue placeholder="Choose supervisor..."/></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {supervisors.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} — {s.email}{s.departments?.length ? ` · ${s.departments.slice(0,2).join(", ")}${s.departments.length > 2 ? "…" : ""}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {resendMode === "manual" && (
                <div className="space-y-2 border-l-2 border-amber-500/60 pl-3">
                  <div className="text-xs text-amber-600">Manual override — email will be sent outside the supervisor list.</div>
                  <Field label="Recipient email">
                    <Input data-testid="resend-manual-email" type="email" value={resendManualEmail} onChange={(e) => setResendManualEmail(e.target.value)} placeholder="name@example.com"/>
                  </Field>
                  <Field label="Recipient name (optional)">
                    <Input data-testid="resend-manual-name" value={resendManualName} onChange={(e) => setResendManualName(e.target.value)} placeholder="Full name"/>
                  </Field>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setResendOpen(false)} disabled={resendBusy}>Cancel</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="resend-send-btn"
                    disabled={resendBusy || !resendPreview}
                    className="gap-1.5"
                  >
                    <MailPlus className="w-4 h-4"/> Send email
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="resend-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send another supervisor notification?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Send another supervisor notification for this request to{" "}
                      <span className="font-semibold">{resendPreview?.name}</span>
                      {" "}(<span className="font-mono">{resendPreview?.email}</span>)?
                      <br/><br/>
                      This will not create a new request, will not modify the original supervisor, and will not change the request status.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={resendBusy}>Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="resend-confirm-btn" onClick={submitResend} disabled={resendBusy}>
                      {resendBusy ? "Sending…" : "Send email"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">{label}</Label>
      {children}
    </div>
  );
}
